<template>
    <x-form ref="entityForm" @submit.prevent="submit">
        <x-container fluid grid-list-lg>
            <x-layout>
                <x-flex xs12>
                    <h2>{{ edit ? "Edit" : "Add" }} Entity</h2>
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="activeEntity.name"
                        label="Name"
                        placeholder=" "
                        required
                        :rules="[(v) => ruleRequired(v, 'Name')]"
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="activeEntity.address.line3"
                        label="Building number"
                        placeholder=" "
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="activeEntity.address.line1"
                        label="Address line 1"
                        placeholder=" "
                        required
                        :rules="[(v) => ruleRequired(v, 'Address line 1')]"
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="activeEntity.address.line2"
                        label="Address line 2"
                        placeholder=" "
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="activeEntity.address.city"
                        label="City/Town"
                        placeholder=" "
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="activeEntity.address.postcode"
                        label="Post code"
                        placeholder=" "
                        required
                        :rules="[(v) => ruleRequired(v, 'Post code')]"
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-select
                        v-model="activeEntity.address.country_id"
                        :items="$root.countries"
                        label="Country"
                        placeholder="Country"
                        item-text="name"
                        item-value="id"
                        :rules="[v => ruleRequired(v, 'Country')]"
                        append-icon="keyboard_arrow_down"
                        outline
                        :disabled="isReadOnly"
                        attach
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="activeEntity.address.latitude"
                        label="Latitude"
                        placeholder=" "
                        outline
                        :rules="[
                            v => ruleLessThanOrEqualTo(v, 90),
                            v => ruleGreaterThanOrEqualTo(v, -90)
                        ]"
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="activeEntity.address.longitude"
                        label="Longitude"
                        placeholder=" "
                        outline
                        :rules="[
                            v => ruleLessThanOrEqualTo(v, 180),
                            v => ruleGreaterThanOrEqualTo(v, -180)
                        ]"
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-text-field
                        v-model="activeEntity.address.duns_ref"
                        label="DUNS reference"
                        placeholder=" "
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs6>
                    <x-text-field
                        ref="hiblClientCodeField"
                        v-model="activeEntity.hibl_client_code"
                        label="HIBL client code"
                        placeholder=" "
                        required
                        maxlength="12"
                        :rules="[
                            (v) => ruleAlphanumeric(v, 'HIBL client code'),
                            (v) => ruleRequiredIfEmpty(v, activeEntity.lux_client_code, 'HIBL client code', 'LUX client code')
                        ]"
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>

                <x-flex xs6>
                    <x-text-field
                        ref="luxClientCodeField"
                        v-model="activeEntity.lux_client_code"
                        label="LUX client code"
                        placeholder=" "
                        required
                        maxlength="12"
                        :rules="[
                            (v) => ruleAlphanumeric(v, 'LUX client code'),
                            (v) => ruleRequiredIfEmpty(v, activeEntity.hibl_client_code, 'LUX client code', 'HIBL client code')
                        ]"
                        outline
                        :disabled="isReadOnly"
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex xs12>
                    <x-select
                        v-model="activeEntity.status"
                        :items="statuses"
                        item-text="name"
                        item-value="type"
                        label="Status"
                        disabled
                        outline
                    />
                </x-flex>
            </x-layout>

            <x-layout>
                <x-flex>
                    <x-btn
                        class="ma-0"
                        color="tertiary"
                        @click="cancelEntityCreation"
                    >
                        Cancel
                    </x-btn>
                </x-flex>

                <x-flex text-xs-right>
                    <x-btn
                        v-if="!isReadOnly"
                        class="ma-0"
                        color="primary"
                        type="submit"
                        :loading="loading"
                    >
                        Save
                    </x-btn>
                </x-flex>
            </x-layout>
        </x-container>

        <modal
            ref="messageDialogModal"
            :title=messageDialogTitle
            :confirmation="showModalConfirmation"
            :cancel-text="'No'"
            max-width="620"
        >
            <ul>
                <li v-for="message in this.messageDialogText">
                    {{ message }}
                </li>
            </ul>
        </modal>
    </x-form>
</template>

<script>
import rules from '~/mixins/rules';
import Modal from '~/components/Shared/UIComponents/Modal';

export default {
    name: 'EntityForm',
    components: {
        Modal
    },
    mixins: [rules],
    props: {
        entity: {
            type: Object,
        },
        edit: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeEntity: this.defaultEntityData(),
            isReadOnly: false,
            messageDialogTitle: '',
            messageDialogText: [],
            loading: false,
            showModalConfirmation: false,
            statuses: [
                {
                    name: 'Active',
                    type: 1
                }
            ],
        };
    },
    watch: {
        'activeEntity.hibl_client_code'() {
            this.$refs.luxClientCodeField.validate();
        },
        'activeEntity.lux_client_code'() {
            this.$refs.hiblClientCodeField.validate();
        }
    },
    mounted() {
        this.$eventBus.$emit('componentWillHandleErrorCodes', {
            componentID: this._uid,
            codesArray: [422]
        });

        this.activeEntity = {...(this.edit ? this.entity : this.defaultEntityData())};

        // prevent entities without address from breaking the form
        if (!this.activeEntity.address) {
            this.activeEntity.address = {};
        }
    },
    methods: {
        defaultEntityData() {
            return {
                address: {},
                status: 1
            }
        },
        async cancelEntityCreation() {
            if (!this.edit && !this.isReadOnly) {
                this.messageDialogText = ['Any unsaved data will be lost. Are you sure you want to cancel?'];
                this.showModalConfirmation = true;
                if (await this.$refs.messageDialogModal.open() === false) {
                    this.showModalConfirmation = false;
                    return;
                }
                this.showModalConfirmation = false;
            }
            this.$emit('close');
        },
        async submit() {
            if (this.$refs.entityForm.validate()) {
                this.loading = true;
                const request = this.edit
                    ? axios.put(this.$root.api(`broker/entity/${this.activeEntity.id}`), this.activeEntity)
                    : axios.post(this.$root.api('broker/entity'), this.activeEntity);

                request.then(async () => {
                    this.$emit('update');

                    this.messageDialogTitle = 'Success';
                    this.messageDialogText = [`Entity ${this.activeEntity.name} has been successfully saved`];
                    this.isReadOnly = true;
                    if (await this.$refs.messageDialogModal.open()) {
                        this.$emit('close');
                    }
                }).catch(res => {
                    if (res.response.status === 422) {
                        const errors = Object.values(res.response.data.errors).flat(Infinity);
                        this.messageDialogText = [];

                        errors.forEach(err => {
                            this.messageDialogText.push(err);
                        })

                        this.messageDialogTitle = 'Error';
                        this.$refs.messageDialogModal.open();
                    }
                }).finally(() => {
                    this.loading = false;
                });
            }
        },
    },
    beforeDestroy() {
        this.$eventBus.$emit('clearComponentWillHandleErrorCodes', this._uid);
    }
};
</script>
